import { Injectable } from "@angular/core";
import { MODEL_API, ORGANISATION_API, RepositoryService } from "../linq-utils/repository.service";
import { CURRENT_STATE_PROJECT_KEY, GraphData, Linqset, LinqsetNode, ORGANISATION_MODEL_KEY, TUTORIAL_MODEL_KEY } from "../common/linqset-model";
import { MatSnackBar } from "@angular/material/snack-bar";
import { OrganisationService } from "../organisation/organisation.service";
import { ORGANISATION_STATE, Organisation, createInsightsFilter } from "../common/organisation";
import { UserService, userUrl } from "../user/user.service";
import { processLinqset, createLinqset } from "../common/model-processing";
import { Router } from "@angular/router";
import { DEFAULT_URL, PROJECTS_URL } from "../app-urls";
import { ONBOARDING_STEPS, User, USER_ROLE } from "../common/user";
import { createLinqsetChanges, getNumberOfChanges, LinqsetChanges } from "../common/linqset-change-model";
import { ERROR_CODE } from "../common/exceptions";
import { combineLinqsetChanges } from "./combine-changes";
import { InsightsRefreshService } from "./insights-refresh.service";

const COMPONENT = "Linqset repository service"

/**
 * Service for getting and saving linqset data to/from the server
 */
// adds the insights the same nodes have elsewhere (the current state's, to a project's) to the nodes, leaving out those
// they already have for the same workspace and model
const addInsights = (nodes:LinqsetNode[], from:LinqsetNode[]|undefined) => {
    for (const n of from || []) {
        const node = nodes.find(x => x._key === n._key)
        if (!node) continue
        node.insights = node.insights || []
        for (const i of n.insights || []) {
            if (!node.insights.some(x => x.projectKey === i.projectKey && x.modelKey === i.modelKey)) node.insights.push(i)
        }
    }
}

@Injectable({
    providedIn: 'root'
  })
export class LinqsetRepositoryService {
    linqset:Linqset|undefined
    comparison:Linqset|undefined

    currentStateOrganisationLinqset:Linqset|undefined
    organisationLinqset:Linqset|undefined

    constructor(private repoService: RepositoryService, private snackBar: MatSnackBar, private router:Router,
        private organisationService: OrganisationService, private userService:UserService, private refreshes:InsightsRefreshService) {
    }

    clear() {
        this.linqset = undefined
        this.comparison = undefined

        this.organisationLinqset = undefined
        this.currentStateOrganisationLinqset = undefined
    }

    async loadOrganisationHomeData(organisationKey:string, workspaceKey:string) {
        const SERVICE = "Load organisation home data"
        const subs = {}
        // clear models so reloads when navigate to a model
        this.clear()

        subs['organisation'] = this.repoService.getData<Organisation>(ORGANISATION_API(organisationKey))
        subs['users'] = this.repoService.getData<User[]>(ORGANISATION_API(organisationKey) + '/users')
        subs['notifications'] =this.repoService.getData<Notification[]>(userUrl(this.userService.getUser()._key) + "/notifications")
        subs['joins'] = this.repoService.getData<GraphData>(ORGANISATION_API(organisationKey, workspaceKey) + "/joins")
        // a project's figures are shown beside the current state's
        if (workspaceKey !== CURRENT_STATE_PROJECT_KEY) subs['currentStateJoins'] = this.repoService.getData<GraphData>(ORGANISATION_API(organisationKey, CURRENT_STATE_PROJECT_KEY) + "/joins")

        const response = await this.repoService.callMultiAPI(subs, COMPONENT, SERVICE, this.userService.getUser(), this.organisationService.organisation)

        if (response?.organisation) this.organisationService.organisation = response.organisation
        const organisation = this.organisationService.organisation

        if (response?.users) this.organisationService.users = response.users
        if (response?.notifications) this.userService.notifications = response.notifications

        if (!organisation) return undefined

        if (organisation.state === ORGANISATION_STATE.EXPIRED && !this.userService.isSysAdmin()) {
            this.snackBar.open("This organisation has expired. Please contact Linq support.", "close");
            this.router.navigateByUrl(DEFAULT_URL())
        }

        if (!organisation.role) {
            this.snackBar.open("You do not have access to this organisation", "close");
            this.router.navigateByUrl("auth")
        }

        const project = organisation.projects?.find(p => p._key === workspaceKey)
        if (project && !project.sandbox && response?.currentStateJoins) this.comparison = createLinqset(organisation, createInsightsFilter(), response.currentStateJoins)

        if (response?.joins) return createLinqset(this.organisationService.organisation, createInsightsFilter(workspaceKey), response.joins)
        return createLinqset(this.organisationService.organisation, createInsightsFilter(workspaceKey))
    }

    async loadModelData(organisationKey:string, modelKey?:string, projectKey?:string, loadOrganisation?:boolean):Promise<Linqset> {
        loadOrganisation = loadOrganisation || (this.linqset && this.linqset.workspaceKey !== (projectKey||CURRENT_STATE_PROJECT_KEY))
        this.linqset = null

        if (modelKey === TUTORIAL_MODEL_KEY) {
            // ensure organisation data is loaded in case of refresh
            await this.loadData(organisationKey, TUTORIAL_MODEL_KEY, CURRENT_STATE_PROJECT_KEY, false)

            const filter = createInsightsFilter()
            filter.modelKeys = [TUTORIAL_MODEL_KEY]
            this.linqset = createLinqset(this.organisationService.organisation, filter)
        }else {
            await this.loadData(organisationKey, modelKey||ORGANISATION_MODEL_KEY, projectKey, loadOrganisation)
        }

        return this.linqset
    }

    async loadModelGraph(organisationKey:string, modelKey:string, projectKey?:string,) {
        const SERVICE = "Load model graph"

        return await this.repoService.callSingleAPI(this.repoService.getData<Linqset>(this.getGraphUrl(organisationKey, modelKey, projectKey)), COMPONENT, SERVICE, this.userService.getUser(), this.organisationService.organisation)
    }

    private async loadData(organisationKey:string, modelKey:string, projectKey?:string, forceOrganisationLoad?:boolean) {
        const SERVICE = "Load data"
        const subs = {}

        const isOrganisation = modelKey === ORGANISATION_MODEL_KEY

        if (forceOrganisationLoad || (!this.organisationService.organisation || this.organisationService.organisation._key !== organisationKey)) {
            subs['organisation'] = this.repoService.getData<Organisation>(ORGANISATION_API(organisationKey))
            subs['users'] = this.repoService.getData<User[]>(ORGANISATION_API(organisationKey) + '/users')
        }

        if (isOrganisation || forceOrganisationLoad || !this.organisationLinqset) {
            subs['organisationModel'] = this.repoService.getData<Linqset>(this.getGraphUrl(organisationKey, ORGANISATION_MODEL_KEY, projectKey))
            subs['joins'] = this.repoService.getData<GraphData>(ORGANISATION_API(organisationKey, projectKey) + "/joins")
            if (isOrganisation)
                subs['captures'] = this.repoService.getData<LinqsetNode[]>(ORGANISATION_API(organisationKey, projectKey||CURRENT_STATE_PROJECT_KEY) + "/capturesandoutcomes")
            // the captures, outcomes and joins come with the workspace's insights only, so in a project the current
            // state's are read too, for the project to be compared with
            if (projectKey && projectKey !== CURRENT_STATE_PROJECT_KEY) {
                subs['currentStateJoins'] = this.repoService.getData<GraphData>(ORGANISATION_API(organisationKey, CURRENT_STATE_PROJECT_KEY) + "/joins")
                if (isOrganisation)
                    subs['currentStateCaptures'] = this.repoService.getData<GraphData>(ORGANISATION_API(organisationKey, CURRENT_STATE_PROJECT_KEY) + "/capturesandoutcomes")
            }

        }

        if (!isOrganisation && modelKey !== TUTORIAL_MODEL_KEY) {
            subs['model'] = this.repoService.getData<Linqset>(this.getGraphUrl(organisationKey, modelKey, projectKey))
        }

        if (Object.keys(subs).length === 0) return

        const response = await this.repoService.callMultiAPI(subs, COMPONENT, SERVICE, this.userService.getUser(), this.organisationService.organisation)

        if (response && response.organisation) this.organisationService.organisation = response.organisation
        if (response && response.users) this.organisationService.users = response.users
        if (response && response.notifications) this.userService.notifications = response.notifications

        const organisation = this.organisationService.organisation
        if (!organisation) return

        if (organisation.state === ORGANISATION_STATE.EXPIRED && !this.userService.isSysAdmin()) {
            this.snackBar.open("This organisation has expired. Please contact Linq support.", "close");
            this.router.navigateByUrl(DEFAULT_URL())
            return
        }

        if (!organisation.role) {
            this.snackBar.open("You do not have access to this organisation", "close");
            this.router.navigateByUrl("auth")
            return
        }

        const project = projectKey && organisation.projects?.find(p => p._key === projectKey)

        if (response.organisationModel) {
            if (response.captures) {
                response.organisationModel.nodes = response.organisationModel.nodes.concat(response.captures.nodes)
                response.organisationModel.edges = response.organisationModel.edges.concat(response.captures.edges)
            }
            if (response.joins) {
                response.organisationModel.nodes = response.organisationModel.nodes.concat(response.joins.nodes)
                response.organisationModel.edges = response.organisationModel.edges.concat(response.joins.edges)
            }

            for (const currentState of [response.currentStateCaptures, response.currentStateJoins]) addInsights(response.organisationModel.nodes, currentState?.nodes)

            this.organisationLinqset = createLinqset(organisation, createInsightsFilter(projectKey), response.organisationModel)

            // if this is for a project create the current state linqset for comparing
            if (project) {
                this.currentStateOrganisationLinqset = createLinqset(organisation, createInsightsFilter(), response.organisationModel)
            }else this.comparison = this.currentStateOrganisationLinqset = undefined

        }

        if (modelKey === TUTORIAL_MODEL_KEY) return

        const model = !isOrganisation && organisation.models?.find(m => m._key === modelKey)

        if (projectKey && projectKey !== CURRENT_STATE_PROJECT_KEY && (!project || !project.role)) {
            const msg = !project?"Project does not exist":"You do not have access to this project"
            this.snackBar.open(msg, "close");
            this.router.navigateByUrl(DEFAULT_URL(organisationKey))
            return
        }

        if (!isOrganisation && (!model || !model.role)) {
            const msg = !model?"Unable to find model for this request.":"You do not have access to this model."
            this.snackBar.open(msg, "close");
            this.router.navigateByUrl(DEFAULT_URL(organisationKey))
            return
        }

        if (model && model.projectKey !== CURRENT_STATE_PROJECT_KEY && model.projectKey !== projectKey) {
            this.snackBar.open("Model is not in this workspace.", "close");
            this.router.navigateByUrl(PROJECTS_URL(organisationKey, projectKey))
            return
        }

        if (isOrganisation) {
            this.linqset = this.organisationLinqset
            this.comparison = this.currentStateOrganisationLinqset
        }else if (response && response.model) {
            this.comparison = undefined
            const filter = createInsightsFilter(projectKey)
            filter.modelKeys = [modelKey]
            this.linqset = createLinqset(organisation, filter, response.model, this.organisationLinqset?.nodes)
            processLinqset(this.linqset, this.organisationLinqset, true, organisation)
            if (project && !project.sandbox && this.currentStateOrganisationLinqset && model.projectKey === CURRENT_STATE_PROJECT_KEY) {
                const csFilter = createInsightsFilter()
                csFilter.modelKeys = [modelKey]
                this.comparison = createLinqset(organisation, csFilter, response.model, this.currentStateOrganisationLinqset.nodes)
            }
        }
    }

    getGraphUrl(organisationKey:string, modelKey:string, projectKey?:string) {
        return MODEL_API(organisationKey, modelKey, projectKey)
    }

    async cleanOrphans(organisationKey:string) {
        try {
            await this.repoService.callSingleAPI(this.repoService.update(`${ORGANISATION_API(organisationKey)}/cleanup`, {}), COMPONENT, "Clean orphans", this.userService.getUser(), this.organisationService.organisation)
            this.snackBar.open("Clean up complete.", "close")
        }catch(err) {
            this.snackBar.open("An error occured while cleaning organisation.", "close")
        }
    }

    // asks the server to recalculate the stored insights for a model, or the whole organisation without one. Resolves
    // once that is done (false if it failed, which is reported here)
    async refreshInsights(linqset?:Linqset):Promise<boolean> {
        if (linqset && !linqset.refresh) return true
        const organisationKey = linqset?.organisationKey || this.organisationService.organisation?._key
        if (!organisationKey) return false
        // only those who can change the organisation may have its insights worked out again (the server refuses anyone
        // else), so for anyone else there's nothing to do, rather than an error
        if (!this.canRefresh(linqset)) return true

        const url = linqset?(linqset.refresh==="organisation"?
            `${ORGANISATION_API(organisationKey, linqset.workspaceKey)}/refresh`:
            `${MODEL_API(organisationKey, linqset.modelKey, linqset.workspaceKey)}/refresh`):
            `${ORGANISATION_API(organisationKey)}/refresh`
        // the organisation without a workspace is its current state
        return this.refresh(url, linqset?.workspaceKey || CURRENT_STATE_PROJECT_KEY)
    }

    // write access to what is refreshed: the organisation, or for a model the model (a LINQ administrator has it all).
    // Unknown, as before the organisation has loaded, is left to the server
    private canRefresh(linqset?:Linqset) {
        if (this.userService.isSysAdmin()) return true
        const role = linqset && linqset.refresh !== 'organisation' ? linqset.model?.role ?? this.organisationService.organisation?.role : this.organisationService.organisation?.role
        return role === undefined || role === null || role <= USER_ROLE.WRITE
    }

    private async refresh(url:string, workspaceKey:string) {
        try {
            await this.refreshes.track(this.repoService.callSingleAPI(this.repoService.update(url, {}), COMPONENT, "Refresh insights", this.userService.getUser(), this.organisationService.organisation, undefined, undefined, false), workspaceKey)
            return true
        }catch(err) {
            this.snackBar.open("An error occurred while refreshing insights. This should be rectified the next time you save; however if you continue to receive this message contact support.", "close")
            return false
        }
    }

    async saveLinqset(linqsetToSave:Linqset, changes?:LinqsetChanges) {
        const SERVICE = "Save this linqset"

        let saveMesage = "Model saved"

        if (linqsetToSave.modelKey === TUTORIAL_MODEL_KEY) {
            linqsetToSave.changes = createLinqsetChanges()
            saveMesage = "You are in a tutorial, so model is not actually saved."
        }else {
            const changesToSave = changes || linqsetToSave.changes

            if (!changesToSave || getNumberOfChanges(changesToSave) === 0) {
                this.snackBar.open("No changes to save", "close");
                return
            }

            // edits made while this saves are collected separately
            if (!changes) linqsetToSave.changes = createLinqsetChanges()

            try {
                if (changesToSave.newModel) {
                    // when moving nodes it also creates a new model. Once it exists, trying again mustn't create it again
                    await this.organisationService.saveModel(changesToSave.newModel, linqsetToSave.workspaceKey, true)
                    changesToSave.newModel = undefined
                }

                await this.repoService.callSingleAPI(this.repoService.update(this.getGraphUrl(linqsetToSave.organisationKey, linqsetToSave.modelKey, linqsetToSave.workspaceKey), changesToSave), COMPONENT, SERVICE, this.userService.getUser(), this.organisationService.organisation, linqsetToSave, undefined,
                    // a node still used elsewhere is explained where the model is saved, which offers to keep editing
                    e => e?.msgCode !== ERROR_CODE.ERR235)
            }catch(err) {
                // not saved: the changes go back on the model so they aren't lost, and saving again includes them
                if (!changes) linqsetToSave.changes = combineLinqsetChanges(changesToSave, linqsetToSave.changes)
                throw err
            }

            this.userService.saveOnboardingStep(ONBOARDING_STEPS.FIRST_MODEL_SAVE)
        }
        this.snackBar.open(saveMesage, "close", {duration:5000});
    }

    async mergeProjectWithCurrentState(organisationKey:string, projectKey:string, modelKey?:string) {
        const url = modelKey?MODEL_API(organisationKey, modelKey, projectKey):ORGANISATION_API(organisationKey, projectKey)

        const SERVICE = "Merge project"

        await this.repoService.callSingleAPI(this.repoService.update(`${url}/replace`, {}), COMPONENT, SERVICE, this.userService.getUser(), this.organisationService.organisation, this.linqset)
        this.snackBar.open("Replaced current state", "close", {duration:5000});
    }

    async mergeNodes(toNodeKey:string, fromNodeKeys:string[]) {
        const SERVICE = "Merge nodes"

        await this.repoService.callSingleAPI(this.repoService.update(`${ORGANISATION_API(this.linqset.organisationKey, this.linqset.workspaceKey)}/mergenodes`, {toNodeKey, fromNodeKeys}), COMPONENT, SERVICE, this.userService.getUser(), this.organisationService.organisation, this.linqset)
        this.snackBar.open("Merged nodes", "close", {duration:5000});
    }
}